// src/components/CustomerJobList.js - WITH PDF DOWNLOAD
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Stack,
  Paper,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search,
  CalendarToday,
  DirectionsCar,
  LocationOn,
  CheckCircle,
  Pending,
  Cancel,
  Download,
  Description,
  Sort,
  Close,
} from "@mui/icons-material";
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { generateJobSheetPDF } from "../utils/jobSheetPDFGenerator";
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers";
import { formatDate, formatTime } from "../utils/formatHelpers";

function CustomerJobList() {
  const { customer } = useCustomerAuth();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("dateDesc");
  const [selectedJob, setSelectedJob] = useState(null);

  // Fetch jobs
  useEffect(() => {
    if (!customer) return;

    const jobsQuery = query(
      collection(db, "jobs"),
      where("clientId", "==", customer.id),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(jobsQuery, (snapshot) => {
      const jobsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setJobs(jobsList);
      setFilteredJobs(jobsList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [customer]);

  // Filter jobs
  useEffect(() => {
    let filtered = [...jobs];

    // Status filter
    if (statusFilter !== "all") {
      if (statusFilter === "active") {
        filtered = filtered.filter(j => 
          ["Pending", "Travelling", "On Site", "In Progress"].includes(j.status)
        );
      } else if (statusFilter === "completed") {
        filtered = filtered.filter(j => j.status === "Completed");
      } else {
        filtered = filtered.filter(j => j.status === statusFilter);
      }
    }

    // Search filter
    if (searchTerm) {
      const lowercasedFilter = searchTerm.toLowerCase();
      filtered = filtered.filter(j =>
        (j.jobTemplateName?.toLowerCase().includes(lowercasedFilter)) ||
        (j.vehicleReg?.toLowerCase().includes(lowercasedFilter)) ||
        (j.siteAddress?.toLowerCase().includes(lowercasedFilter)) ||
        (j.jobReferenceNumber?.toString().toLowerCase().includes(lowercasedFilter)) ||
        (j.endCustomerName?.toLowerCase().includes(lowercasedFilter)) ||
        (j.engineerName?.toLowerCase().includes(lowercasedFilter))
      );
    }

    // Sort logic
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "dateAsc":
          return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
        case "status":
          return (a.status || "").localeCompare(b.status || "");
        case "dateDesc":
        default:
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      }
    });

    setFilteredJobs(filtered);
  }, [jobs, searchTerm, statusFilter, sortBy]);

  const handleDownloadJobSheet = async (job) => {
    const result = await generateJobSheetPDF(job);
    if (!result.success) {
      alert("Failed to generate PDF: " + result.error);
    }
  };

  // Calculate stats
  const totalJobs = filteredJobs.length;
  const activeJobs = filteredJobs.filter(j => 
    ["Pending", "Travelling", "On Site", "In Progress"].includes(j.status)
  ).length;
  const completedJobs = filteredJobs.filter(j => j.status === "Completed").length;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: "white", fontWeight: "bold" }} gutterBottom>
          My Jobs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and track all your service jobs
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Paper
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              Total Jobs
            </Typography>
            <Typography variant="h3" fontWeight="bold">
              {totalJobs}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              Active Jobs
            </Typography>
            <Typography variant="h3" fontWeight="bold">
              {activeJobs}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              Completed
            </Typography>
            <Typography variant="h3" fontWeight="bold">
              {completedJobs}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by job, vehicle, address, engineer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Filter by Status"
            >
              <MenuItem value="all">All Jobs</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="Incomplete">Incomplete</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Sort By"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Sort /></InputAdornment>
                ),
              }}
            >
              <MenuItem value="dateDesc">Newest First</MenuItem>
              <MenuItem value="dateAsc">Oldest First</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Jobs List */}
      {filteredJobs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Description sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No jobs found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {jobs.length === 0 
              ? "You don't have any jobs yet"
              : "Try adjusting your search or filters"}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {filteredJobs.map((job) => (
            <Card
              key={job.id}
              sx={{
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => setSelectedJob(job)}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  {/* Job Info */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {job.jobTemplateName || "Service"}
                        </Typography>
                        <Chip
                          icon={getStatusIcon(job.status)}
                          label={job.status || "Pending"}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary">
                        Job #{job.jobReferenceNumber || "N/A"}
                      </Typography>

                      {job.vehicleReg && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <DirectionsCar fontSize="small" color="action" />
                          <Typography variant="body2">
                            {job.vehicleReg}
                            {job.vehicleMake && ` - ${job.vehicleMake}`}
                          </Typography>
                        </Box>
                      )}

                      {job.siteAddress && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {job.siteAddress}
                          </Typography>
                        </Box>
                      )}

                      {job.engineerName && (
                        <Typography variant="body2" color="text.secondary">
                          Engineer: {job.engineerName}
                        </Typography>
                      )}
                    </Stack>
                  </Grid>

                  {/* Date & Actions */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1} alignItems={{ xs: "flex-start", md: "flex-end" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2">
                          {job.scheduledDateTime 
                            ? `${formatDate(job.scheduledDateTime)} at ${formatTime(job.scheduledDateTime)}`
                            : formatDate(job.createdAt)}
                        </Typography>
                      </Box>

                      {job.customerNotes && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          Note: {job.customerNotes}
                        </Typography>
                      )}

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadJobSheet(job);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Download Job Sheet
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Job Details Dialog */}
      <Dialog
        open={Boolean(selectedJob)}
        onClose={() => setSelectedJob(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedJob && (
          <>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {selectedJob.jobTemplateName || "Job Details"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ref: #{selectedJob.jobReferenceNumber}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedJob(null)} size="small">
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Chip 
                        label={selectedJob.status} 
                        color={getStatusColor(selectedJob.status)} 
                        size="small" 
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Date & Time</Typography>
                      <Typography variant="body1">
                        {selectedJob.scheduledDateTime 
                          ? `${formatDate(selectedJob.scheduledDateTime)} at ${formatTime(selectedJob.scheduledDateTime)}`
                          : formatDate(selectedJob.createdAt)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Vehicle</Typography>
                      <Typography variant="body1">{selectedJob.vehicleReg || "N/A"}</Typography>
                      {selectedJob.vehicleMake && (
                        <Typography variant="body2" color="text.secondary">{selectedJob.vehicleMake}</Typography>
                      )}
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                      <Typography variant="body1">{selectedJob.siteAddress || "N/A"}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Engineer</Typography>
                      <Typography variant="body1">{selectedJob.engineerName || "Unassigned"}</Typography>
                    </Box>
                    {selectedJob.customerNotes && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Your Notes</Typography>
                        <Typography variant="body1">{selectedJob.customerNotes}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
              <Button 
                startIcon={<Download />} 
                onClick={() => handleDownloadJobSheet(selectedJob)}
              >
                Download Job Sheet
              </Button>
              <Button onClick={() => setSelectedJob(null)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default CustomerJobList;